import React, { useEffect, useState } from 'react';
import { Button, useTheme } from '@mui/material';
import ListSubheader from '@mui/material/ListSubheader';
import List from '@mui/material/List';
import Collapse from '@mui/material/Collapse';
import { useQueryNotifications } from '../../../queries/UseQueryNotifications';
import { useOrganizations } from '../../../queries/organizations/UseOrganizations';
import OrganizationHierarchyUtils from '../OrganizationHierarchyUtils';
import { IOrganization } from '../../../services/organization-data-service/Models';
import { MobileOrganizationListItem } from './MobileOrganizationListItem';

export type MobileOrganizationHierarchyProps = {
    onSelected: (selectedItem: IOrganization) => void;
    onLoading?: (isLoading: boolean) => void;
    organizationID: number;    
    includeAllOption?: boolean;
};

export default function MobileOrganizationHierarchy({
    onSelected, 
    onLoading,
    organizationID,
    includeAllOption
}: MobileOrganizationHierarchyProps) {

    const theme = useTheme();
    const [open, setOpen] = useState(true);

    const [expandedItem, setExpandedItem] = useState<IOrganization>();

    const [currentOrganizationID, setCurrentOrganizationID] = useState(organizationID)

    const {data: organizations} = useQueryNotifications(useOrganizations)({
        includeAllOption
    });

    const treeData = OrganizationHierarchyUtils.getTreeData(organizations, expandedItem?.id ?? currentOrganizationID);
    const ancestors = OrganizationHierarchyUtils.getAncestors(organizations, expandedItem?.id ?? currentOrganizationID);

    useEffect(() => {

        if (onLoading) {
            onLoading(treeData === undefined);
        }
    }, [treeData,
        onLoading]);    


    const onBack =  async (item: IOrganization) => {

        setCurrentOrganizationID(item.id);
        setExpandedItem(undefined);
    };

    const onExpand = async (item: IOrganization) => {        

        setExpandedItem(item);
        setOpen(true);
    };

    const onToggleItem = async (item: IOrganization) => {
    
        if (!item.parentID) {
            setOpen(!open);
        } else {
            const parent = organizations?.find(o => o.id === item.parentID);
            if (parent) {
                await onBack(parent);
            }
        }
    };

    
    const onSelectItem = async (item: IOrganization) => {
        
        if (treeData?.id !== item.id){
            await onExpand(item);
        }

        setExpandedItem(undefined);

        onSelected(item);
    };

    return (
        <List
            sx={{
                marginTop: theme.spacing(1),
                flexGrow: 1,
                width: '100%',
                backgroundColor: theme.palette.background.paper,
                overflowY: 'auto'
            }}
            component="nav"
            aria-labelledby="ancestor-list"
            subheader={
                <ListSubheader component="div" id="ancestor-list" 
                    sx={{
                        display: 'flex',
                        flexDirection:'column'
                    }}
                    >
                    {
                        ancestors?.map((item, index) => 
                            <Button key={item.id}
                                sx={{
                                    '& + &': {
                                        marginTop: theme.spacing(1)
                                    }
                                }}
                                fullWidth
                                variant="contained"
                                color="secondary"
                                onClick={(e) => { e.persist(); onBack(item)}}>
                                {`${item.displayText}/`}
                            </Button>
                        )
                    }
                
                </ListSubheader>
        }>
            <MobileOrganizationListItem data={treeData as IOrganization} 
                        open={open} 
                        onToggle={onToggleItem} 
                        onSelected={onSelectItem} />

            <Collapse  in={open} timeout="auto" unmountOnExit>
                <List 
                    sx={{
                        backgroundColor: theme.palette.background.paper,
                    }}
                    component="div" disablePadding>
                    {
                        treeData?.children?.map((item) => 
                            <MobileOrganizationListItem key={item.id} 
                                sx={{
                                    paddingLeft: theme.spacing(4),                                    
                                }}
                                data={item}
                                open={false}
                                onToggle={onExpand}
                                onSelected={onSelectItem} />
                        )
                    }
                </List>
            </Collapse>            
        </List>
    );
}
